import React from "react";
import { Link } from "react-router-dom";
import {
  STEWARD_ENTITY,
  FOUNDER_NAME,
  FOUNDATION_EMAIL,
} from "@shared/campaign";

/**
 * StewardshipDisclosure — the unmissable, plainly-worded block that names
 * Mind Hack, Inc. as the temporary fiduciary steward, states that gifts are
 * not tax-deductible during the stewardship period, and links to the
 * Foundation's transparency ledger.
 *
 * This is the first thing a prospective supporter should read before any
 * direct-give CTA. Honest framing is what makes the stewardship arrangement
 * legitimate; trying to soften it would undermine the trust the rest of the
 * page is built on.
 */
export function StewardshipDisclosure() {
  return (
    <aside
      aria-labelledby="stewardship-heading"
      className="border border-hairline bg-paper-2 p-8 md:p-10"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-state-pending font-bold mb-3 block">
        Stewardship notice · please read before giving
      </span>
      <h2
        id="stewardship-heading"
        className="text-2xl font-serif text-ink mb-4 max-w-2xl"
      >
        Funds are held by {STEWARD_ENTITY} as temporary fiduciary steward.
      </h2>
      <div className="text-ink-2 font-serif leading-relaxed space-y-4 max-w-3xl">
        <p>
          The .person Foundation is not yet a US 501(c)(3); Form 1023 has not
          been filed and the IRS determination has not arrived. In the interim,{" "}
          <strong className="text-ink">{STEWARD_ENTITY}</strong> &mdash; a
          Delaware C-corporation owned by the Founding Director,{" "}
          {FOUNDER_NAME} &mdash; acts as the temporary fiduciary steward for
          monetary support. The stewardship period ends on IRS determination,
          when funds and obligations transfer to the Foundation.
        </p>
        <p>
          <strong className="text-ink">
            Gifts during the stewardship period are not tax-deductible.
          </strong>{" "}
          Some gifts may be eligible for retroactive treatment after
          determination is granted, but no specific outcome can be guaranteed.
          Please consult your own tax advisor.
        </p>
        <p>
          Every wire and every expenditure under stewardship is recorded and
          published on the{" "}
          <Link
            to="/foundation/ledger"
            className="text-ink underline decoration-hairline hover:decoration-ink"
          >
            public ledger
          </Link>
          . Transparency during this transition is what makes the arrangement
          honest rather than theoretical &mdash; if anything on this page is
          unclear,{" "}
          <a
            href={`mailto:${FOUNDATION_EMAIL}?subject=Stewardship%20question`}
            className="text-ink underline decoration-hairline hover:decoration-ink"
          >
            write to us
          </a>{" "}
          before you give.
        </p>
      </div>
    </aside>
  );
}
